import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useState } from 'react';
import {
  useGetCustomerQuery,
  useGetCustomerHistoryQuery,
  useRecordCustomerPaymentMutation,
} from '../../../store/api/salesApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { useGetPaymentAccountsQuery } from '../../../store/api/purchaseApi';

function CustomerDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);

  const { data: customer, isLoading: loadingCustomer, error: customerError } = useGetCustomerQuery(id);
  const { data: history, isLoading: loadingHistory, error: historyError } = useGetCustomerHistoryQuery(id);
  const { data: company } = useGetCurrentCompanyQuery();
  const { data: paymentAccounts } = useGetPaymentAccountsQuery();
  const [recordCustomerPayment, { isLoading: savingPayment }] = useRecordCustomerPaymentMutation();
  const currency = company?.currency || 'USD';

  const formatAmount = (value) => {
    const amount = Number(value || 0);
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency , minimumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2), maximumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)}).format(amount);
    } catch {
      return `${currency} ${amount.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}`;
    }
  };

  const salesOrders = history?.salesOrders || [];
  const openBalance = salesOrders.reduce(
    (sum, so) => sum + (so.salesType === 'credit' ? Number(so.balanceAmount || 0) : 0),
    0
  );

  const [receiveOpen, setReceiveOpen] = useState(false);
  const [receiveForm, setReceiveForm] = useState({
    paymentDate: new Date().toISOString().split('T')[0],
    amount: '',
    paymentMethod: 'cash',
    paymentAccountId: '',
    referenceNo: '',
    notes: '',
  });

  if (loadingCustomer || loadingHistory) {
    return <Typography>Loading...</Typography>;
  }
  if (customerError) {
    return <Typography color="error">Error loading customer</Typography>;
  }
  if (historyError) {
    return <Typography color="error">Error loading sales history</Typography>;
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 4,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate('/customers')} size="small">
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              {customer?.name || 'Customer'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {customer?.code && `Code: ${customer.code}  · `}
              {customer?.email || customer?.phone || ''}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {`Outstanding Credit Balance: ${formatAmount(openBalance)}`}
            </Typography>
            {customer?.locationUrl && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Location:{' '}
                <a href={customer.locationUrl} target="_blank" rel="noopener noreferrer">
                  Open map
                </a>
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ mt: { xs: 2, sm: 0 } }}>
          <Button
            variant="contained"
            disabled={openBalance <= 0}
            sx={{ borderRadius: 2 }}
            onClick={() => {
              setReceiveForm((prev) => ({
                ...prev,
                paymentDate: new Date().toISOString().split('T')[0],
                amount: openBalance > 0 ? openBalance.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)) : '',
                paymentMethod: 'cash',
                paymentAccountId: '',
                referenceNo: '',
                notes: '',
              }));
              setReceiveOpen(true);
            }}
          >
            Receive Overall Payment
          </Button>
        </Box>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Sales Orders" />
      </Tabs>

      {tab === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Sales Orders
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Order Number</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Invoice Number</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Invoice Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Sales Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{`Total (${currency})`}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{`Balance (${currency})`}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {salesOrders.length > 0 ? (
                    salesOrders.map((so) => (
                      <TableRow key={so.id} hover>
                        <TableCell sx={{ fontWeight: 500 }}>{so.orderNumber}</TableCell>
                        <TableCell>{so.invoiceNumber || '-'}</TableCell>
                        <TableCell>
                          {so.invoiceDate ? new Date(so.invoiceDate).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{so.salesType}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{formatAmount(so.totalAmount)}</TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{formatAmount(so.balanceAmount)}</TableCell>
                        <TableCell>{so.status}</TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/sales/orders/${so.id}`)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">No sales orders for this customer.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
      <Dialog
        open={receiveOpen}
        onClose={() => setReceiveOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1.5 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Receive Payment from Customer</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            type="date"
            label="Payment Date *"
            value={receiveForm.paymentDate}
            onChange={(e) => setReceiveForm((prev) => ({ ...prev, paymentDate: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            select
            label="Method *"
            value={receiveForm.paymentMethod}
            onChange={(e) =>
              setReceiveForm((prev) => ({ ...prev, paymentMethod: e.target.value }))
            }
            fullWidth
          >
            <MenuItem value="cash">Cash</MenuItem>
            <MenuItem value="bank">Bank</MenuItem>
            <MenuItem value="card">Card</MenuItem>
            <MenuItem value="cheque">Cheque</MenuItem>
          </TextField>
          <TextField
            select
            label="Account (optional)"
            value={receiveForm.paymentAccountId}
            onChange={(e) =>
              setReceiveForm((prev) => ({ ...prev, paymentAccountId: e.target.value }))
            }
            fullWidth
          >
            <MenuItem value="">Auto (by method)</MenuItem>
            {paymentAccounts?.map((acc) => (
              <MenuItem key={acc.id} value={acc.id}>
                {acc.code} - {acc.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            type="number"
            label={`Amount (${currency}) *`}
            value={receiveForm.amount}
            onChange={(e) => setReceiveForm((prev) => ({ ...prev, amount: e.target.value }))}
            fullWidth
            inputProps={{ min: 0, step: 0.01 }}
          />
          <TextField
            label="Reference"
            value={receiveForm.referenceNo}
            onChange={(e) => setReceiveForm((prev) => ({ ...prev, referenceNo: e.target.value }))}
            fullWidth
          />
          <TextField
            label="Notes"
            value={receiveForm.notes}
            onChange={(e) => setReceiveForm((prev) => ({ ...prev, notes: e.target.value }))}
            fullWidth
            multiline
            rows={2}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1, pb: 1 }}>
            <Button onClick={() => setReceiveOpen(false)} disabled={savingPayment}>
              Close
            </Button>
            <Button
              variant="contained"
              disabled={savingPayment}
              onClick={async () => {
                const amt = Number(receiveForm.amount || 0);
                if (!amt || amt <= 0) {
                  alert('Amount must be greater than zero');
                  return;
                }
                try {
                  await recordCustomerPayment({
                    id,
                    paymentDate: receiveForm.paymentDate,
                    amount: amt,
                    paymentMethod: receiveForm.paymentMethod,
                    paymentAccountId: receiveForm.paymentAccountId || undefined,
                    referenceNo: receiveForm.referenceNo || undefined,
                    notes: receiveForm.notes || undefined,
                  }).unwrap();
                  setReceiveOpen(false);
                } catch (err) {
                  alert(err?.data?.message || 'Error recording customer payment');
                }
              }}
            >
              {savingPayment ? 'Saving...' : 'Save Payment'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default CustomerDetails;

